<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import dayjs from "dayjs";
import useAuthenticatedFetch from "@/components/frontend/composable/useAuthenticatedFetch";
import { useNotification } from "@kyvg/vue3-notification";

const { notify } = useNotification();

const props = defineProps({
  tour: {
    required: true,
    type: String
  },
  locale: {
    required: true,
    type: String
  }
});

const { authenticatedFetch, isLoading } = useAuthenticatedFetch(props.tour, props.locale);

const bookings = ref([]);

const fetchData = async () => {
  try {
    const response = await authenticatedFetch('booking');

    const { data } = await response.json();

    bookings.value = data;

  } catch (error) {
    console.error("Fetch Data Error:", error);
  }
};

const statusColor = (status) => {
  if (status.value === "validated") return "bg-success";
  if (status.value === "pending") return "bg-warning";
  return "bg-danger";
};

const formattedDate = (date, format) => {
  return dayjs(date).format(format);
};

const hasBookable = computed(() => {
  if (bookings.value.some(booking => booking.booking_slot?.stage)) return "stage";
  if (bookings.value.some(booking => booking.store)) return "store";
  return null;
});

const cancelBooking = async (booking) => {
  if (!booking.is_cancelable) return;
  try {
    const response = await authenticatedFetch(`booking/${booking.uuid}/cancel`, "POST");
    const data = await response.json();

    if (response.ok) {
      await fetchData();
      notify({
        group: "errors",
        type: "success",
        title: data.message
      });
    } else {
      notify({
        group: "errors",
        type: "error",
        title: data.message.booking ? data.message.booking[0] : data.message,
      });
    }

  } catch (error) {
    notify({
      group: "errors",
      type: "error",
      title: error.message
    });
  }
};

onMounted(fetchData);
</script>

<template>
  <div style="position: relative;">
    <template v-if="isLoading">
      <div class="d-flex justify-content-center" style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(255, 255, 255, 0.7); z-index: 10; align-items: center;">
        <span class="spinner-border text-primary"></span>
      </div>
    </template>
    <div class="table-responsive">
      <table class="table table-hover align-middle">
        <thead>
        <tr>
          <th>{{ $t("Product") }}</th>
          <th>{{ $t("Booking dates") }}</th>
          <template v-if="hasBookable">
            <th>
              <template v-if="hasBookable === 'stage'">{{ $t("Stage") }}</template>
              <template v-else-if="hasBookable === 'store'">{{ $t("Store") }}</template>
            </th>
          </template>
          <th>{{ $t("Registration") }}</th>
          <th>{{ $t("Status") }}</th>
          <th>{{ $t("Created at") }}</th>
          <th>{{ $t("Actions") }}</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="booking in bookings" :key="booking.id">
          <td>
            <template v-if="booking.equipment">
              <strong>{{ booking.equipment?.product.name || 'N/A' }}</strong>
              <span class="text-muted"> - {{ booking.equipment.size }}</span>
              <br>
              {{ booking.equipment?.product?.brand?.name || 'N/A' }}
            </template>
            <template v-else>
              N/A
            </template>
          </td>

          <td>
            <strong>{{ formattedDate(booking.start_at, "ll") }}</strong> <small class="text-muted">{{ formattedDate(booking.start_at, "LT") }}</small>
            <br>
            <strong>{{ formattedDate(booking.end_at, "ll") }}</strong> <small class="text-muted">{{ formattedDate(booking.end_at, "LT") }}</small>
          </td>

          <template v-if="hasBookable">
            <td>
              <template v-if="hasBookable === 'stage'">{{ booking.booking_slot?.stage.name }}</template>
              <template v-else-if="hasBookable === 'store'">{{ booking.store.name }}</template>
            </td>
          </template>

          <td>
            {{ booking.registration.firstname }} {{ booking.registration.lastname }}
          </td>
          <td>
            <span class="badge text-white fs-8"
                  :class="statusColor(booking.status)">
              {{ booking.status.label }}
            </span>
          </td>
          <td>
            <small class="text-muted">{{ formattedDate(booking.created_at, "ll") }}</small>
          </td>
          <td>

            <button
              type="button"
              class="btn btn-primary btn-block"
              :disabled="!booking.is_cancelable"
              @click.prevent="cancelBooking(booking)">
                {{ $t('Cancel') }}
            </button>
          </td>

        </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>

</style>
